import {
  PendingQuestionDisplay,
  PendingQuestionStepAction,
} from '@activepieces/shared';
import { t } from 'i18next';
import { X } from 'lucide-react';

/**
 * One line above the chat input naming what the next message relates to: the
 * run/step the user asked about, or the question the server is waiting on.
 * Shared so the two never drift into two different looks.
 */
export type ContextChipProps = {
  label: string;
  /** Absent when the label is not itself a link to something to reopen */
  onClick?: () => void;
  onDismiss: () => void;
};

/**
 * One literal `t('...')` call per step action: Tolgee's extraction reads the
 * source statically and cannot follow a key looked up from a table.
 */
const stepActionReplyLabel = (action: PendingQuestionStepAction): string => {
  switch (action) {
    case 'test':
      return t('Reply to: testing the step');
    case 'fix_failed_test':
      return t('Reply to: fixing the failed test');
    case 'complete_router_condition':
      return t('Reply to: the router condition');
    case 'review_write':
      return t('Reply to: running the write');
  }
};

/**
 * What the reply chip says, by subject kind. `framing`'s question is the
 * model's own free text, never translated.
 */
export const replyChipLabel = ({
  subject,
  stepDisplayName,
}: PendingQuestionDisplay): string => {
  switch (subject.kind) {
    case 'field':
      return stepDisplayName
        ? t('Reply to: {field} ({step})', {
            field: subject.label,
            step: stepDisplayName,
          })
        : t('Reply to: {field}', { field: subject.label });
    case 'connection':
      return t('Reply to: connection for {piece}', {
        piece: subject.pieceDisplayName,
      });
    case 'step_action':
      return stepActionReplyLabel(subject.action);
    case 'framing':
      return subject.question;
  }
};

export const ContextChip = ({
  label,
  onClick,
  onDismiss,
}: ContextChipProps) => (
  <div className="flex items-center justify-between gap-2 px-3 pt-2 text-xs text-muted-foreground">
    {onClick ? (
      <button
        type="button"
        className="truncate text-left hover:underline"
        onClick={onClick}
      >
        {label}
      </button>
    ) : (
      <span className="truncate text-left">{label}</span>
    )}
    <button type="button" aria-label={t('Remove')} onClick={onDismiss}>
      <X size={14} />
    </button>
  </div>
);
